// Photo cell for `RequestBoxesView` read-only item rows. Extracted as
// part of the #1178 pre-decomposition (§7 step 5) so the parent file
// shrinks below the file-size cap before the regroup edit-mode UI
// lands. Shape identical to the previous inline component — no
// behavioral change.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../../../ui/index.js';
import { requestBoxItemPhotoDownloadUrl } from '../../../../api/requests/index.js';
import { usePhotoDownloadResolver } from '../../photoDownloadResolver.js';

// Same shape gate as `ItemsTab.isIssuedPhotoKey`: only round-trip to
// the signed-URL endpoint for keys that match the issued S3 layout,
// so a malformed/legacy key falls through to a silent placeholder
// rather than a noisy 404.
const ISSUED_PHOTO_KEY_RE = /^request-item-photo\/[0-9a-f-]{36}\/[0-9a-f-]{36}$/i;

export interface BoxItemThumbProps {
  requestId: string;
  boxId: string;
  boxItemId: string;
  objectKey: string | null;
}

export function BoxItemThumb({ requestId, boxId, boxItemId, objectKey }: BoxItemThumbProps) {
  const { t } = useTranslation(['requests']);
  // WHY (#2140): resolve through the injected snapshot resolver when present
  //   (archive view); absent (live view), behavior is byte-identical.
  const resolver = usePhotoDownloadResolver();
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    setDownloadUrl(null);
    if (!objectKey || !ISSUED_PHOTO_KEY_RE.test(objectKey)) return () => undefined;
    const p = resolver
      ? resolver(objectKey)
      : requestBoxItemPhotoDownloadUrl(requestId, boxId, boxItemId, objectKey).then(
          (r) => r.downloadUrl,
        );
    void p
      .then((url) => {
        if (alive) setDownloadUrl(url);
      })
      .catch(() => {
        if (alive) setDownloadUrl(null);
      });
    return () => {
      alive = false;
    };
  }, [requestId, boxId, boxItemId, objectKey, resolver]);
  if (downloadUrl) {
    return (
      <a
        href={downloadUrl}
        target="_blank"
        rel="noreferrer"
        style={{
          width: 56,
          height: 42,
          borderRadius: 4,
          overflow: 'hidden',
          border: '1px solid var(--border-2)',
          background: 'var(--bg-2)',
          flex: '0 0 auto',
        }}
        aria-label={t('requests:draft.boxes.itemPhoto')}
      >
        <img
          src={downloadUrl}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </a>
    );
  }
  return (
    <span
      title={objectKey ? undefined : t('requests:draft.boxes.itemPhotoMissing')}
      style={{
        width: 56,
        height: 42,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 4,
        border: '1px solid var(--border-2)',
        background: 'var(--bg-2)',
        color: objectKey ? 'var(--status-success-fg)' : 'var(--fg-4)',
        flex: '0 0 auto',
      }}
    >
      <Icon name={objectKey ? 'image' : 'image-plus'} size={16} />
    </span>
  );
}
